import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { NavPageWrapper } from '../common/Nav';
import { fetchStatusQuestionsAction, clearStatusQuestionsAction } from './redux/fetchStatusQuestions';
import { fetchSingleOpportunityAction, clearSingleOpportunityAction } from './redux/fetchSingleOpportunity';
import { fetchOpportunityAnswersAction } from './redux/fetchOpportunityAnswers';
import QuestionSection from './QuestionSection';
import CallDetailsSection from './CallDetailsSection';
import OpportunityHeader from './OpportunityHeader';
import OpportunityFooter from './OpportunityFooter';
import { postOpportunityAction } from './redux/postOpportunity';
import { fetchAgentsAction } from './redux/fetchAgents';
import { fetchTeamsAction } from './redux/fetchTeams';
import { fetchCallDetailsAction } from './redux/fetchCallDetails';
import { fetchKpisAction } from './redux/fetchKpis';
import { fetchFormTypesAction } from './redux/fetchFormTypes';
import { fetchReasonsAction } from './redux/fetchReasons';
import { postExistingOpportunityAction } from './redux/postExistingOpportunity';
import {
  ROUTES,
  STATUS_IDS,
  SECTION_IDS,
  MAX_STATUS_ID,
} from './redux/constants';

class DivComponent extends PureComponent {
  render() {
    return (
      <div className={this.props.className}>
        {this.props.children}
      </div>
    );
  };
}

export class OpportunityPage extends PureComponent {
  state = {
    postData: null,
    isErrorTriggered: false,
    showPrintText: false,
    coachingFormType: 0,
  }

  /**
   * Handle fetching data and setting up section validity ref
   */
  componentDidMount() {
    const {
      isNewOpportunity,
      fetchNewOpportunityData,
      fetchExistingOpportunityData,
    } = this.props;

    // fetch the data
    isNewOpportunity ? fetchNewOpportunityData() : fetchExistingOpportunityData();

    // set up validity ref
    this.sectionIdValidMap = Object.values(SECTION_IDS).reduce((result, sectionId) => {
      // the call details section will automatically be valid if not a new opportunity
      result[sectionId] = sectionId === SECTION_IDS.callDetailsSection && !isNewOpportunity;

      return result;
    }, {});
  }

  componentWillUnmount() {
    this.props.clearOpportunityPage();
  }

  componentDidUpdate(prevProps, prevState) {
    const { postedOpportunity, singleOpportunity, historyPush, isNewOpportunity, isTeamLead, fetchAgents, teams } = this.props;

    //non team leads do not have a teamID, need to grab sample from teams
    if (isNewOpportunity && !isTeamLead && JSON.stringify(prevProps.teams) !== JSON.stringify(teams) && Array.isArray(teams)) {
      const teamId = teams[0].id;
      fetchAgents({ teamId });
    }

    //grabbing existing teamId in a posted opportunity
    if (!isNewOpportunity && !prevProps.singleOpportunity && singleOpportunity && singleOpportunity.salesAgent) {
      //update coaching form type to show questions properly
      this.setState({
        coachingFormType: singleOpportunity.coachingFormType.id,
      });
      if (singleOpportunity.salesAgent) {
        const isTeamLead = singleOpportunity.salesAgent.agentPosition === 2;
        const teamId = isTeamLead ? singleOpportunity.salesAgent.teamLeadTeams[0].id : singleOpportunity.salesAgent.teamId;
        fetchAgents({ teamId });
      }
    }

    //changing the team dropdown when making new opportunity
    if(isNewOpportunity && !isTeamLead && this.state.postData 
      && ((!prevState.postData && this.state.postData.team) || (!!prevState.postData && prevState.postData.team !== this.state.postData.team))) {
      const teamId = this.state.postData.team;
      fetchAgents({ teamId});
    }

    //redirect/reload to posted opportunity page if new opportunity
    if (!prevProps.postedOpportunity && postedOpportunity) {
      if (postedOpportunity.id && isNewOpportunity) {
        historyPush(`/AgentCoaching/Opportunity/${postedOpportunity.id}`);
      } else {
        historyPush(ROUTES.agentCoachingPage);
      }
      window.location.reload();
    }
  }

  filterStatusQuestions = (statusQuestions) => {
    return Array.isArray(statusQuestions) ? statusQuestions.filter(question => question.coachingFormType.id === this.state.coachingFormType) : [];
  }

  handlePrintToggle = (value, res) => {
    this.setState({ showPrintText: value }, res);
  }

  /**
   * handles incoming section data
   * @param {*} value - the section value
   * @param {string} sectionId
   * @param {boolean} isSectionValid 
   */
  handleEvent = (value, sectionId, isSectionValid) => {
    const postData = {
      ...this.state.postData,
      ...value,
    };

    //remove unneeded fields from process flow evaluation forms
    if (value.coachingFormType && value.coachingFormType.id === 3) {
      if (postData.dateOfCall) delete postData.dateOfCall;
      if (postData.callLogId) delete postData.callLogId;
    }
    // update values
    this.setState({ postData });
    // update validity
    this.sectionIdValidMap = { ...this.sectionIdValidMap, [sectionId]: isSectionValid };
  }

  handleFormTypeChange = (coachingFormType) => {
    this.setState({ coachingFormType });
  }

  /**
   * Handles cancel (only on new opportunity, not editing existing)
   */
  handleCancel = () => {
    this.props.historyPush(ROUTES.agentCoachingPage);
  }

  /**
   * Handles opportunity submission; impossible to submit if opportunity is already completed
   */
  handleSubmit = () => {
    const { postData, coachingFormType } = this.state;
    
    const {
      postOpportunity,
      isNewOpportunity,
      singleOpportunity,
      postExistingOpportunity,
      user: { supervisorAgentID: teamLeadId = 0 },
    } = this.props;
    
    const areAllSectionsValid = Object.values(this.sectionIdValidMap).every((val) => val);
    
    if (!areAllSectionsValid) {
      return this.setState({ isErrorTriggered: true });
    }

    if (isNewOpportunity) {
      // safety check to delete values for process flow evaluations
      if (coachingFormType === 3) {
        if (postData.dateOfCall) delete postData.dateOfCall;
        if (postData.callLogId) delete postData.callLogId;
      }

      postData["originateTeamLead"] = { id: teamLeadId };

      const { answers, ...coachingOpportunity } = postData;
      
      return postOpportunity({
        coachingOpportunity,
        answers: answers.filter(answer => answer.answer && (answer.question.coachingFormType.id === coachingFormType)),
      });
    }

    // remember it's impossible to get to this point in the code w/o all these items being present
    return postExistingOpportunity(
      postData.answers,
      singleOpportunity.id,
      singleOpportunity.status.id + 1
    );
  }

  render() {
    const { isErrorTriggered, showPrintText, coachingFormType } = this.state;
    const {
      kpis,
      path,
      teams,
      agents,
      reasons,
      agentName,
      uiError,
      formTypes,
      teamLeadName,
      isTeamLead,
      isDataReady,
      callDetails,
      isCompleted,
      statusQuestions,
      fetchAgents,
      fetchCallDetails,
      isNewOpportunity,
      singleOpportunity,
      opportunityAnswers,
    } = this.props;

    const filteredQuestions = this.filterStatusQuestions(statusQuestions);

    return (
      <NavPageWrapper wrapperClassNames="ac-op__nav-wrap" currPath={path}>
        {isDataReady && (
          <DivComponent 
            className="ac-op"
            ref={el => (this.componentRef = el)}
          >
            <OpportunityHeader 
              isNewOpportunity={isNewOpportunity}
              isCompleted={isCompleted}
              componentRef={this.componentRef}
              handlePrintToggle={this.handlePrintToggle}
            />
            <CallDetailsSection
              kpis={kpis}
              agents={agents}
              reasons={reasons}
              formTypes={formTypes}
              teams={teams}
              uiError={uiError}
              callDetails={callDetails}
              isDisabled={!isNewOpportunity}
              isTeamLead={isTeamLead}
              eventHandler={this.handleEvent}
              handleFormTypeChange={this.handleFormTypeChange}
              isErrorTriggered={isErrorTriggered}
              fetchCallDetails={fetchCallDetails}
              fetchAgents={fetchAgents}
              sectionId={SECTION_IDS.callDetailsSection}
              singleOpportunity={singleOpportunity}
            />
            <QuestionSection
              questions={filteredQuestions}
              eventHandler={this.handleEvent}
              isErrorTriggered={isErrorTriggered}
              answeredQuestions={opportunityAnswers}
              sectionId={SECTION_IDS.questionSection}
              isCompleted={isCompleted}
              coachingFormType={coachingFormType}
            />
            {!isCompleted && !showPrintText && (
              <OpportunityFooter
                handleCancel={this.handleCancel}
                handleSubmit={this.handleSubmit}
                isNewOpportunity={isNewOpportunity}
              />
            )}
            {showPrintText && (
              <div className="signature-text">
                <div className="signature">
                  <div className="underline">
                    X
                  </div>
                  <div>
                    {teamLeadName}
                  </div>
                </div>
                <div className="signature">
                  <div className="underline">
                    X
                  </div>
                  <div>
                    {agentName}
                  </div>
                </div>
              </div>
            )}
          </DivComponent>
        )}
      </NavPageWrapper>
    );
  }
}

OpportunityPage.propTypes = {
  uiError: PropTypes.object,
  teamId: PropTypes.number,
  kpis: PropTypes.array,
  agents: PropTypes.array,
  reasons: PropTypes.array,
  teams: PropTypes.array,
  formTypes: PropTypes.array,
  statusQuestions: PropTypes.array,
  path: PropTypes.string.isRequired,
  singleOpportunity: PropTypes.object,
  postedOpportunity: PropTypes.object,
  opportunityAnswers: PropTypes.oneOfType([ PropTypes.array, PropTypes.object ]),
  location: PropTypes.object.isRequired,
  isDataReady: PropTypes.bool.isRequired,
  isCompleted: PropTypes.bool.isRequired,
  historyPush: PropTypes.func.isRequired,
  postOpportunity: PropTypes.func.isRequired,
  isNewOpportunity: PropTypes.bool.isRequired,
  fetchCallDetails: PropTypes.func.isRequired,
  fetchNewOpportunityData: PropTypes.func.isRequired,
  fetchExistingOpportunityData: PropTypes.func.isRequired,
  postExistingOpportunity: PropTypes.func.isRequired,
};

OpportunityPage.defaultProps = {
  teamId: null,
  teams: null,
  kpis: null,
  formTypes: null,
  agents: null,
  reasons: null,
  uiError: null,
  statusQuestions: null,
  singleOpportunity: null,
  postedOpportunity: null,
  opportunityAnswers: null,
};

/* istanbul ignore next */
function mapStateToProps({
  common: { user: { teamManagerType } },
  agentCoaching: {
    teamId,
    teams,
    kpis,
    agents,
    reasons,
    formTypes,
    uiError,
    callDetails,
    statusQuestions,
    singleOpportunity,
    postedOpportunity,
    opportunityAnswers,
  }
}, ownProps) {
  const isTeamLead = teamManagerType === 0;
  const isNewOpportunity = !ownProps.match.params.opportunityId;
  const isCompleted = Boolean(singleOpportunity && singleOpportunity.status.id === MAX_STATUS_ID);
  const teamLeadName = singleOpportunity
    && singleOpportunity.originateTeamLead
    && singleOpportunity.originateTeamLead.fullName
    ? singleOpportunity.originateTeamLead.fullName : '';
  const agentName = singleOpportunity
    && singleOpportunity.salesAgent
    && singleOpportunity.salesAgent.fullName
    ? singleOpportunity.salesAgent.fullName : '';

  // a cheap, easy (lazy) way to only display UI when all data is fetched
  const isDataReady = isNewOpportunity
    ? Boolean(teams && agents && reasons && kpis && formTypes && statusQuestions)
    : Boolean((teams && agents && reasons && kpis && formTypes && opportunityAnswers && singleOpportunity && (isCompleted || statusQuestions)));

    return {
      kpis,
      teamId,
      teams,
      agents,
      reasons,
      formTypes,
      uiError,
      isTeamLead,
      isDataReady,
      callDetails,
      isCompleted,
      teamLeadName,
      agentName,
      statusQuestions,
      isNewOpportunity,
      singleOpportunity,
      postedOpportunity,
      opportunityAnswers,
      path: ownProps.match.path,
      historyPush: ownProps.history.push,
    }
}

/* istanbul ignore next */
function mapDispatchToProps(dispatch, ownProps) {
  // abstract this non-ui logic from component, making fetch calls easier
  return {
    clearOpportunityPage: () => {
      dispatch(clearStatusQuestionsAction());
      dispatch(clearSingleOpportunityAction());
    },
    fetchCallDetails: (filters) => dispatch(fetchCallDetailsAction(filters)),
    fetchAgents: (filters) => dispatch(fetchAgentsAction(filters)),
    fetchNewOpportunityData: () => {
      dispatch(clearSingleOpportunityAction());
      dispatch(fetchKpisAction());
      dispatch(fetchReasonsAction());
      dispatch(fetchTeamsAction());
      dispatch(fetchFormTypesAction());
      if(ownProps.user.teamID) dispatch(fetchAgentsAction({ teamId: ownProps.user.teamID }));
      dispatch(fetchStatusQuestionsAction({ statusId: STATUS_IDS.new }));
    },
    fetchExistingOpportunityData: () => {
      const opportunityId = ownProps.match.params.opportunityId;
      
      dispatch(fetchKpisAction());
      dispatch(fetchReasonsAction());
      dispatch(fetchFormTypesAction());
      dispatch(fetchTeamsAction());
      if(ownProps.user.teamID) dispatch(fetchAgentsAction({ teamId: ownProps.user.teamID }));
      dispatch(fetchSingleOpportunityAction({ opportunityId }));
      dispatch(fetchOpportunityAnswersAction({ opportunityId }));
    },
    postOpportunity: (requestBody) => (
      dispatch(postOpportunityAction(requestBody, ownProps.user.userName))
    ),
    postExistingOpportunity: (requestBody, id, targetStatusId) => (
      dispatch(postExistingOpportunityAction({
        requestBody,
        targetStatusId,
        opportunityId: id,
        requestedBy: ownProps.user.userName,
      }))
    ),
  };
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(OpportunityPage);



// WEBPACK FOOTER //
// ./src/features/agent-coaching/OpportunityPage.js